import { useTranslation } from 'react-i18next';
import React from 'react';
import { ScheduledOrder } from '../types';
import dayjs from '@jobcost-monorepo/dayjs-config';
import type { Dayjs } from 'dayjs';
import {
  cn,
  countBusinessDays,
  currencyFormatter,
} from '@jobcost-monorepo/utils';

interface IProps {
  dataList: ScheduledOrder[];
}

const OrdersScheduledBody = React.forwardRef<HTMLDivElement, IProps>(
  ({ dataList }, ref) => {
    if (dataList.length === 0) return <div />;
    const { t } = useTranslation('orders');
    let start = '';
    let end = '';
    let totalCostNet = 0;
    let totalRevenueNet = 0;
    let totalCostGross = 0;
    let totalRevenueGross = 0;
    let totalProfit = 0;

    const handleGetDays = (
      month: Dayjs,
      startOrder: Date | null,
      endOrder: Date | null,
    ): number => {
      try {
        const startOfMonth = month.startOf('month');
        const endOfMonth = month.endOf('month');

        const startDate = startOrder ? dayjs.utc(startOrder) : null;
        const endDate = endOrder ? dayjs.utc(endOrder) : null;

        // Se startOrder o endOrder sono null, considera l'intero mese
        if (!startDate || !endDate) {
          return countBusinessDays(startOfMonth, endOfMonth);
        }

        // Caso 1: Se startOrder è prima dell'inizio del mese e endOrder è dopo la fine del mese
        if (startDate.isBefore(startOfMonth) && endDate.isAfter(endOfMonth)) {
          return countBusinessDays(startOfMonth, endOfMonth);
        }

        // Caso 2: Se startOrder è dopo la fine del mese
        if (startDate.isAfter(endOfMonth) || endDate.isBefore(startOfMonth)) {
          return countBusinessDays(startOfMonth, endOfMonth);
        }

        // Caso 3: Se startOrder ed endOrder sono entrambi nel mese considerato
        const start = startDate.isBefore(startOfMonth)
          ? startOfMonth
          : startDate;
        const end = endDate.isAfter(endOfMonth) ? endOfMonth : endDate;

        return countBusinessDays(start, end);
      } catch (e) {
        return 0;
      }
    };

    return (
      <div ref={ref} className="w-full">
        {dataList.map((groupedDate, index) => {
          if (index === 0) {
            start = groupedDate.monthAndYear;
          }
          if (dataList.length === index + 1) {
            end = groupedDate.monthAndYear;
          }
          let totalMonthlyCostNet = 0;
          let totalMonthlyRevenueNet = 0;
          let totalMonthlyCostGross = 0;
          let totalMonthlyRevenueGross = 0;
          let totalMonthlyProfit = 0;

          return (
            <div key={groupedDate.monthAndYear} className="mb-4 max-w-[10rem]">
              <div className="text-lg font-bold mb-2">
                {dayjs
                  .utc(`${groupedDate.monthAndYear}-01`)
                  .locale('it')
                  .format('YYYY-MMMM')}
              </div>
              <div className="table w-full">
                <div className="table-header-group">
                  <div className="table-row">
                    <div className="table-cell rounded-tl-lg border-t border-l border-black dark:border-grey50 p-2 font-bold whitespace-nowrap">
                      {t('analysis.order')}
                    </div>
                    <div className="table-cell border-t border-black dark:border-grey50 p-2 font-bold whitespace-nowrap">
                      {t('analysis.startDate')}
                    </div>
                    <div className="table-cell border-t border-black dark:border-grey50 p-2 font-bold whitespace-nowrap">
                      {t('analysis.endDate')}
                    </div>
                    <div className="table-cell border-t border-black dark:border-grey50 p-2 font-bold whitespace-nowrap">
                      {t('analysis.supplier')}
                    </div>
                    <div className="table-cell border-t border-black dark:border-grey50 p-2 font-bold whitespace-nowrap">
                      {t('analysis.resource')}
                    </div>
                    <div className="table-cell border-t border-black dark:border-grey50 p-2 font-bold whitespace-nowrap text-right">
                      {t('analysis.hoursAllocatedPerDay')}
                    </div>
                    <div className="table-cell border-t border-black dark:border-grey50 p-2 font-bold whitespace-nowrap text-right">
                      {t('analysis.dailyCost')}
                    </div>
                    <div className="table-cell border-t border-black dark:border-grey50 p-2 font-bold whitespace-nowrap text-right">
                      {t('analysis.dailyRevenue')}
                    </div>
                    <div className="table-cell border-t border-black dark:border-grey50 p-2 font-bold whitespace-nowrap text-right">
                      {t('analysis.days')}
                    </div>
                    <div className="table-cell border-t border-black dark:border-grey50 p-2 font-bold whitespace-nowrap text-right">
                      {t('analysis.monthlyCostNet')}
                    </div>
                    <div className="table-cell border-t border-black dark:border-grey50 p-2 font-bold whitespace-nowrap text-right">
                      {t('analysis.monthlyRevenueNet')}
                    </div>
                    <div className="table-cell border-t border-black dark:border-grey50 p-2 font-bold whitespace-nowrap text-right">
                      {t('analysis.monthlyCostGross')}
                    </div>
                    <div className="table-cell border-t border-black dark:border-grey50 p-2 font-bold whitespace-nowrap text-right">
                      {t('analysis.monthlyRevenueGross')}
                    </div>
                    <div className="table-cell rounded-tr-lg border-t border-r border-black dark:border-grey50 p-2 font-bold whitespace-nowrap text-right">
                      {t('analysis.monthlyProfit')}
                    </div>
                  </div>
                </div>
                <div className="table-row-group w-full">
                  {groupedDate.orders.map((order, index) => {
                    const hoursAllocatedPerDay =
                      order.hoursAllocatedPerDay ?? 0;
                    const hourCost = order.hourCost ?? 0;
                    const hourRevenue = order.hourRevenue ?? 0;
                    const days = handleGetDays(
                      dayjs.utc(`${groupedDate.monthAndYear}-01`),
                      order.startDate,
                      order.endDate,
                    );
                    const dailyCost = hourCost * hoursAllocatedPerDay;
                    const dailyRevenue = hourRevenue * hoursAllocatedPerDay;
                    const monthlyCostNet = dailyCost * days;
                    const monthlyRevenueNet = dailyRevenue * days;
                    const monthlyCostGross = monthlyCostNet * 1.22;
                    const monthlyRevenueGross = monthlyRevenueNet * 1.22;
                    const monthlyProfit = monthlyRevenueNet - monthlyCostNet;
                    totalMonthlyCostNet = totalMonthlyCostNet + monthlyCostNet;
                    totalMonthlyRevenueNet =
                      totalMonthlyRevenueNet + monthlyRevenueNet;
                    totalMonthlyCostGross =
                      totalMonthlyCostGross + monthlyCostGross;
                    totalMonthlyRevenueGross =
                      totalMonthlyRevenueGross + monthlyRevenueGross;
                    totalMonthlyProfit = totalMonthlyProfit + monthlyProfit;
                    totalCostNet = totalCostNet + monthlyCostNet;
                    totalRevenueNet = totalRevenueNet + monthlyRevenueNet;
                    totalCostGross = totalCostGross + monthlyCostGross;
                    totalRevenueGross = totalRevenueGross + monthlyRevenueGross;
                    totalProfit = totalProfit + monthlyProfit;

                    const expiringClass = dayjs
                      .utc(order.endDate)
                      .isSame(
                        dayjs.utc(`${groupedDate.monthAndYear}-01`),
                        'month',
                      )
                      ? 'font-bold text-red'
                      : '';

                    return (
                      <div
                        key={`${groupedDate.monthAndYear}-${order.id}`}
                        className={cn(
                          index % 2
                            ? `table-row bg-violet2 dark:bg-transparent ${expiringClass}`
                            : `table-row bg-violet5 dark:bg-darkGrey ${expiringClass}`,
                          groupedDate.orders.length === index + 1 &&
                            'rounded-bl-lg',
                        )}
                      >
                        <div
                          className={cn(
                            'table-cell border-l border-black dark:border-grey50 p-2 whitespace-nowrap',
                            groupedDate.orders.length === index + 1 &&
                              'border-b rounded-bl-lg',
                          )}
                        >
                          {order.code}
                        </div>
                        <div
                          className={cn(
                            'table-cell p-2 whitespace-nowrap',
                            groupedDate.orders.length === index + 1 &&
                              'border-b border-black dark:border-grey50',
                          )}
                        >
                          {dayjs.utc(order.startDate).format('DD-MM-YYYY')}
                        </div>
                        <div
                          className={cn(
                            'table-cell p-2 whitespace-nowrap',
                            groupedDate.orders.length === index + 1 &&
                              'border-b border-black dark:border-grey50',
                          )}
                        >
                          <span
                            className={
                              dayjs
                                .utc(order.endDate)
                                .isSame(
                                  dayjs.utc(`${groupedDate.monthAndYear}-01`),
                                  'month',
                                )
                                ? 'font-bold text-red'
                                : ''
                            }
                          >
                            {dayjs.utc(order.endDate).format('DD-MM-YYYY')}
                          </span>
                        </div>
                        <div
                          className={cn(
                            'table-cell p-2 whitespace-nowrap',
                            groupedDate.orders.length === index + 1 &&
                              'border-b border-black dark:border-grey50',
                          )}
                        >
                          {order.supplier?.name}
                        </div>
                        <div
                          className={cn(
                            'table-cell p-2 whitespace-nowrap',
                            groupedDate.orders.length === index + 1 &&
                              'border-b border-black dark:border-grey50',
                          )}
                        >
                          {order.resource?.name}
                        </div>
                        <div
                          className={cn(
                            'table-cell p-2 whitespace-nowrap text-right',
                            groupedDate.orders.length === index + 1 &&
                              'border-b border-black dark:border-grey50',
                          )}
                        >
                          {order.hoursAllocatedPerDay}
                        </div>
                        <div
                          className={cn(
                            'table-cell p-2 whitespace-nowrap text-right',
                            groupedDate.orders.length === index + 1 &&
                              'border-b border-black dark:border-grey50',
                          )}
                        >
                          {currencyFormatter({
                            value: dailyCost,
                            options: {},
                          })}
                        </div>
                        <div
                          className={cn(
                            'table-cell p-2 whitespace-nowrap text-right',
                            groupedDate.orders.length === index + 1 &&
                              'border-b border-black dark:border-grey50',
                          )}
                        >
                          {currencyFormatter({
                            value: dailyRevenue,
                            options: {},
                          })}
                        </div>
                        <div
                          className={cn(
                            'table-cell p-2 whitespace-nowrap text-right',
                            groupedDate.orders.length === index + 1 &&
                              'border-b border-black dark:border-grey50',
                          )}
                        >
                          {days}
                        </div>
                        <div className="table-cell p-2 whitespace-nowrap text-right">
                          {currencyFormatter({
                            value: monthlyCostNet,
                            options: {},
                          })}
                        </div>
                        <div className="table-cell p-2 whitespace-nowrap text-right">
                          {currencyFormatter({
                            value: monthlyCostGross,
                            options: {},
                          })}
                        </div>
                        <div className="table-cell p-2 whitespace-nowrap text-right">
                          {currencyFormatter({
                            value: monthlyRevenueNet,
                            options: {},
                          })}
                        </div>
                        <div className="table-cell p-2 whitespace-nowrap text-right">
                          {currencyFormatter({
                            value: monthlyRevenueGross,
                            options: {},
                          })}
                        </div>
                        <div className="table-cell border-r border-black dark:border-grey50 p-2 whitespace-nowrap text-right">
                          {currencyFormatter({
                            value: monthlyProfit,
                            options: {},
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="table-row-group">
                  <div
                    key={`${groupedDate.monthAndYear}-totals`}
                    className="table-row"
                  >
                    <div className="table-cell"></div>
                    <div className="table-cell"></div>
                    <div className="table-cell"></div>
                    <div className="table-cell"></div>
                    <div className="table-cell"></div>
                    <div className="table-cell"></div>
                    <div className="table-cell"></div>
                    <div className="table-cell"></div>
                    <div className="table-cell"></div>
                    <div
                      className={cn(
                        'table-cell rounded-bl-lg border-l border-b border-black dark:border-grey50 p-2 font-bold whitespace-nowrap text-right',
                        groupedDate.orders.length % 2
                          ? 'bg-violet2 dark:bg-transparent'
                          : 'bg-violet5 dark:bg-darkGrey',
                      )}
                    >
                      {currencyFormatter({
                        value: totalMonthlyCostNet,
                        options: {},
                      })}
                    </div>
                    <div
                      className={cn(
                        'table-cell border-b border-black dark:border-grey50 p-2 font-bold whitespace-nowrap text-right',
                        groupedDate.orders.length % 2
                          ? 'bg-violet2 dark:bg-transparent'
                          : 'bg-violet5 dark:bg-darkGrey',
                      )}
                    >
                      {currencyFormatter({
                        value: totalMonthlyCostGross,
                        options: {},
                      })}
                    </div>
                    <div
                      className={cn(
                        'table-cell border-b border-black dark:border-grey50 p-2 font-bold whitespace-nowrap text-right',
                        groupedDate.orders.length % 2
                          ? 'bg-violet2 dark:bg-transparent'
                          : 'bg-violet5 dark:bg-darkGrey',
                      )}
                    >
                      {currencyFormatter({
                        value: totalMonthlyRevenueNet,
                        options: {},
                      })}
                    </div>
                    <div
                      className={cn(
                        'table-cell border-b border-black dark:border-grey50 p-2 font-bold whitespace-nowrap text-right',
                        groupedDate.orders.length % 2
                          ? 'bg-violet2 dark:bg-transparent'
                          : 'bg-violet5 dark:bg-darkGrey',
                      )}
                    >
                      {currencyFormatter({
                        value: totalMonthlyRevenueGross,
                        options: {},
                      })}
                    </div>
                    <div
                      className={cn(
                        'table-cell border-b border-r rounded-br-lg border-black dark:border-grey50 p-2 font-bold whitespace-nowrap text-right',
                        groupedDate.orders.length % 2
                          ? 'bg-violet2 dark:bg-transparent'
                          : 'bg-violet5 dark:bg-darkGrey',
                      )}
                    >
                      {currencyFormatter({
                        value: totalMonthlyProfit,
                        options: {},
                      })}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}

        {start !== end && (
          <div className="pb-5 min-w-[600px] max-w-[1474px]">
            <h2 className="text-lg font-bold">
              {dayjs.utc(`${start}-01`).locale('it').format('MMMM') +
                '-' +
                dayjs.utc(`${end}-01`).locale('it').format('MMMM YYYY')}
            </h2>
            <div className="table w-full">
              <div className="table-header-group">
                <div className="table-row">
                  <div className="table-cell rounded-tl-lg border-l border-t p-2 font-bold border-black dark:border-grey50">
                    {t('analysis.monthlyCostNet')}
                  </div>
                  <div className="table-cell p-2 border-t border-black dark:border-grey50 font-bold">
                    {t('analysis.monthlyRevenueNet')}
                  </div>
                  <div className="table-cell p-2 border-t border-black dark:border-grey50 font-bold">
                    {t('analysis.monthlyCostGross')}
                  </div>
                  <div className="table-cell p-2 border-t border-black dark:border-grey50 font-bold">
                    {t('analysis.monthlyRevenueGross')}
                  </div>
                  <div className="table-cell p-2 rounded-tr-lg border-t border-r border-black dark:border-grey50 font-bold">
                    {t('analysis.profit')}
                  </div>
                </div>
              </div>
              <div className="table-row-group">
                <div className="table-row">
                  <div className="table-cell border-l rounded-bl-lg border-b p-2 border-black dark:border-grey50 bg-violet5 dark:bg-darkGrey">
                    {currencyFormatter({
                      value: totalCostNet,
                      options: {},
                    })}
                  </div>
                  <div className="table-cell p-2 border-b border-black dark:border-grey50 bg-violet5 dark:bg-darkGrey">
                    {currencyFormatter({
                      value: totalCostGross,
                      options: {},
                    })}
                  </div>
                  <div className="table-cell p-2 border-b border-black dark:border-grey50 bg-violet5 dark:bg-darkGrey">
                    {currencyFormatter({
                      value: totalRevenueNet,
                      options: {},
                    })}
                  </div>
                  <div className="table-cell p-2 border-b border-black dark:border-grey50 bg-violet5 dark:bg-darkGrey">
                    {currencyFormatter({
                      value: totalRevenueGross,
                      options: {},
                    })}
                  </div>
                  <div className="table-cell p-2 rounded-br-lg border-b border-r border-black dark:border-grey50 bg-violet5 dark:bg-darkGrey">
                    {currencyFormatter({
                      value: totalProfit,
                      options: {},
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  },
);

OrdersScheduledBody.displayName = 'OrdersScheduledBody';

export default OrdersScheduledBody;
